import { useCallback, useEffect, useRef, useState } from 'react';

const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])', 'select:not([disabled])',
  'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

export function focusableWithin(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (node) => !node.matches(':disabled') && (node.offsetParent !== null || node === document.activeElement),
  );
}

/**
 * Traps Tab inside `ref`, moves initial focus into it, and returns focus to
 * whatever was focused when the layer opened. Every overlay in the prototype
 * uses this — a dialog that steals focus and does not give it back is the most
 * common keyboard failure in an editor shell.
 */
export function useFocusTrap(active: boolean, ref: React.RefObject<HTMLElement | null>, onDismiss: () => void): void {
  const restoreRef = useRef<HTMLElement | null>(null);
  const restoreAncestors = useRef<HTMLElement[]>([]);

  useEffect(() => {
    if (!active) return;
    restoreRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    restoreAncestors.current = [];
    for (let ancestor = restoreRef.current?.parentElement; ancestor; ancestor = ancestor.parentElement) restoreAncestors.current.push(ancestor);
    const root = ref.current;
    if (root) {
      const first = focusableWithin(root)[0];
      (first ?? root).focus();
    }
    return () => {
      const restore = restoreRef.current;
      if (restore && document.contains(restore) && restore.getClientRects().length && !restore.matches(':disabled')) {
        restore.focus();
        if (document.activeElement === restore) return;
      }
      // Responsive layouts can hide the launcher while a modal is open. Keep
      // keyboard focus in its nearest surviving region instead of losing it to
      // the body or a hidden menu item. Skip the modal being removed.
      for (const ancestor of restoreAncestors.current) {
        if (!document.contains(ancestor)) continue;
        const fallback = focusableWithin(ancestor).find(node => !root?.contains(node));
        if (fallback) { fallback.focus(); if (document.activeElement === fallback) return; }
      }
    };
  }, [active, ref]);

  useEffect(() => {
    if (!active) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.isComposing || event.keyCode === 229) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onDismiss();
        return;
      }
      if (event.key !== 'Tab') return;
      const root = ref.current;
      if (!root) return;
      const items = focusableWithin(root);
      if (items.length === 0) {
        event.preventDefault();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const current = document.activeElement;
      if (event.shiftKey && (current === first || !root.contains(current))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && current === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [active, ref, onDismiss]);
}

/**
 * Roving tab order for a tablist or menu: exactly one item is tabbable, arrows
 * move between items, Home/End jump to the ends. Returns the index that should
 * carry `tabIndex={0}` plus the key handler.
 */
export function useRoving(count: number, activeIndex: number, onSelect: (index: number) => void, orientation: 'horizontal' | 'vertical' = 'horizontal') {
  const [focusIndex, setFocusIndex] = useState(activeIndex);
  useEffect(() => { setFocusIndex(activeIndex); }, [activeIndex]);

  const prevKey = orientation === 'horizontal' ? 'ArrowLeft' : 'ArrowUp';
  const nextKey = orientation === 'horizontal' ? 'ArrowRight' : 'ArrowDown';

  const onKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (count === 0) return;
    const target = event.target instanceof Element ? event.target.closest('[role="tab"],[role="radio"],[role="menuitemradio"]') : null;
    if (!target || target.parentElement !== event.currentTarget || event.altKey || event.ctrlKey || event.metaKey) return;
    let next: number | null = null;
    if (event.key === prevKey) next = (focusIndex - 1 + count) % count;
    else if (event.key === nextKey) next = (focusIndex + 1) % count;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = count - 1;
    if (next === null) return;
    event.preventDefault();
    event.stopPropagation();
    setFocusIndex(next);
    onSelect(next);
    const container = event.currentTarget;
    window.requestAnimationFrame(() => {
      const items = container.querySelectorAll<HTMLElement>('[role="tab"],[role="radio"],[role="menuitemradio"]');
      items[next]?.focus();
    });
  }, [count, focusIndex, onSelect, prevKey, nextKey]);

  return { focusIndex, onKeyDown };
}

/** Closes a non-modal layer on outside pointer-down or Escape. */
export function useDismissable(active: boolean, ref: React.RefObject<HTMLElement | null>, onDismiss: () => void): void {
  useEffect(() => {
    if (!active) return;
    const onDown = (event: PointerEvent) => {
      const root = ref.current;
      if (root && event.target instanceof Node && !root.contains(event.target)) onDismiss();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); onDismiss(); }
    };
    window.addEventListener('pointerdown', onDown, true);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [active, ref, onDismiss]);
}

/** Polite live-region announcer. One node, re-announced on change. */
export function useAnnouncer(): [string, (message: string) => void] {
  const [message, setMessage] = useState('');
  const announce = useCallback((next: string) => {
    setMessage('');
    window.requestAnimationFrame(() => setMessage(next));
  }, []);
  return [message, announce];
}
